/** @jsxImportSource @emotion/react */

import { css } from "@emotion/react";

import { useTranslation } from "react-i18next";

import Typography from "@mui/material/Typography";

import { colors } from "@dotnet/telematics-design-system";

import GlobalScheduleColumnIcon from "../../../../assets/customIcons/schedules/GlobalScheduleColumnIcon";
import MultipleScheduleColumnIcon from "../../../../assets/customIcons/schedules/MultipleScheduleColumnIcon";
import NoSchedulesColumnIcon from "../../../../assets/customIcons/schedules/NoSchedulesColumnIcon";
import QuickDownloadColumnIcon from "../../../../assets/customIcons/schedules/QuickDownloadColumnIcon";
import SingleScheduleColumnIcon from "../../../../assets/customIcons/schedules/SingleScheduleColumnIcon";
import { ACTIVITY_STATUS } from "../../../../shared";
import IconWithTooltip from "../../../IconsWithTooltips/IconWithTooltip/IconWithTooltip";
import { useStyles } from "../../styles";
import { SCHEDULES_SCOPE } from "../../types";

export type SchedulesParamsType = {
  id: string;
  schedules: string[];
  status: string;
};

type ScheduleSummariesType = {
  name: string;
  scope: number;
};

interface SchedulesListProps {
  schedulesParams: SchedulesParamsType;
  scheduleSummaries: ScheduleSummariesType[];
}

const SchedulesList = ({
  schedulesParams,
  scheduleSummaries
}: SchedulesListProps) => {
  const { t } = useTranslation();

  const { scheduleColumnIcon, multipleRowsTooltip, multipleSchedulesIcon } =
    useStyles();

  const { status } = schedulesParams;
  const isRowDisabled = status === ACTIVITY_STATUS.Deactivated;
  const disabledColor = isRowDisabled ? colors.textDisabled : undefined;

  // Group schedules by type
  const processSchedules = () => {
    const result: (ScheduleSummariesType | ScheduleSummariesType[])[] = [];
    const otherSchedules: ScheduleSummariesType[] = [];
    let quickDownloadFound = false;

    // Global schedule is always displayed first, then custom schedules, then quick schedule
    scheduleSummaries?.forEach(schedule => {
      if (schedule.scope === SCHEDULES_SCOPE.GlobalSchedule) {
        result.push([schedule]);
      } else if (schedule.scope === SCHEDULES_SCOPE.QuickSchedule) {
        quickDownloadFound = true;
      } else {
        // create stack of custom schedules, since they should be displayed together
        otherSchedules.push(schedule);
      }
    });

    if (otherSchedules.length > 0) {
      result.push(otherSchedules);
    }

    if (quickDownloadFound) {
      result.push([
        {
          name: t(`Table##Quick schedule`),
          scope: SCHEDULES_SCOPE.QuickSchedule
        }
      ]);
    }

    return result;
  };

  const renderListOfSchedules = () => {
    let count = 1;
    return processSchedules().map(schedule => {
      count++;
      return <div key={count}>{generateScheduleIcon(schedule)}</div>;
    });
  };

  const generateNoSchedulesView = () =>
    scheduleSummaries?.length === 0 && (
      <IconWithTooltip
        iconTestId="no-schedules-icon"
        icon={<NoSchedulesColumnIcon />}
        title={t("Table##No schedule applied")}
        customIconStyle={{ height: "24px", width: "24px" }}
      />
    );

  const generateScheduleIcon = (schedule: any) => {
    if (schedule?.length === 1) {
      switch (schedule[0].scope) {
        case SCHEDULES_SCOPE.GlobalSchedule:
          return (
            <IconWithTooltip
              iconTestId="global-schedule-icon"
              icon={
                <GlobalScheduleColumnIcon
                  currentColor={disabledColor}
                  css={css(scheduleColumnIcon)}
                />
              }
              title={t(`Table##Global schedule`)}
            />
          );
        case SCHEDULES_SCOPE.QuickSchedule:
          return (
            <IconWithTooltip
              iconTestId="Quick-schedule-icon"
              icon={
                <QuickDownloadColumnIcon
                  currentColor={disabledColor}
                  css={css(scheduleColumnIcon)}
                />
              }
              title={t(`Table##Quick schedule`)}
            />
          );
        default:
          return (
            <IconWithTooltip
              iconTestId="single-schedule-icon"
              icon={
                <SingleScheduleColumnIcon
                  currentColor={disabledColor}
                  css={css(scheduleColumnIcon)}
                />
              }
              title={schedule[0].name} // title is custom. It is not translatable.
            />
          );
      }
    } else {
      return (
        <IconWithTooltip
          iconTestId="multiple-schedule-icon"
          icon={
            <MultipleScheduleColumnIcon
              currentColor={disabledColor}
              css={css(multipleSchedulesIcon)}
            />
          }
          title={
            <div css={css(multipleRowsTooltip)}>
              {schedule.map((item: ScheduleSummariesType) => (
                <Typography key={item.name} variant="body2Regular">
                  {item.name}
                </Typography>
              ))}
            </div>
          }
        />
      );
    }
  };

  return (
    <div css={css({ display: "flex", alignItems: "center" })}>
      {generateNoSchedulesView() || renderListOfSchedules()}
    </div>
  );
};

export default SchedulesList;
